import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled from 'styled-components';

import { CatalogGrid } from '@/components/CatalogCard/CatalogGrid';
import FollowSellerButton from '@/components/FollowSellerButton/FollowSellerButton';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchUpcomingCatalogsIfNeeded } from '@/redux/modules/upcomingCatalogs';
import { getCatalogUrl } from '@/utils/urls';

import useGetUpcomingOrLiveCatalogs from '../../hooks/useGetUpcomingOrLiveCatalogs';

const MAX_TODAY_AUCTIONS = 50;

type Props = {
    catalogId: number;
};

const AuctionIsDone = ({ catalogId }: Props) => {
    const dispatch = useAppDispatch();
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const name = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const [hasLiveCatalogs, catalogIds] = useGetUpcomingOrLiveCatalogs();

    React.useEffect(() => {
        dispatch(fetchUpcomingCatalogsIfNeeded(MAX_TODAY_AUCTIONS, true));
    }, [dispatch]);

    return (
        <StyledDoneDiv>
            <StyledHeaderDiv>
                <FormattedMessage
                    id="bidding_console.done.title"
                    values={{
                        title: (
                            <RouteLink
                                key="bidding-console-done-title-link"
                                to={getCatalogUrl(catalogId, title)}
                            >
                                {title}
                            </RouteLink>
                        ),
                    }}
                />
            </StyledHeaderDiv>
            <StyledCta>
                <H2>
                    <FormattedMessage
                        id="bidding_console.done.status_message"
                        values={{ name }}
                    />
                </H2>
                <FollowSellerButton sellerId={sellerId} />
            </StyledCta>
            <StyledLiveAuctions>
                <H2>
                    {hasLiveCatalogs ? (
                        <FormattedMessage id="bidding_console.done.live_catalogs_title" />
                    ) : (
                        <FormattedMessage id="bidding_console.done.upcoming_catalogs_title" />
                    )}
                </H2>
                <CatalogGrid
                    catalogCardClass="mx-0 mb-0 max-w-[calc(100%+20px)] flex-[1_0_auto] border-b-0 mdMin:m-0 mdMin:px-[14px] mdMin:py-[20px]"
                    catalogIds={catalogIds}
                    className="mx-[-10px] w-[calc(100%+20px)]"
                />
            </StyledLiveAuctions>
        </StyledDoneDiv>
    );
};

export default AuctionIsDone;

const StyledDoneDiv = styled.div`
    display: block;
    height: 100%;
    overflow: auto;
`;

const StyledHeaderDiv = styled.div`
    margin: 0 0 32px;
    padding: 8px 24px;
    display: inline-block;
    position: relative;
    text-align: center;
    color: ${({ theme }) => theme.colors.black};
    transition: all 0.2s;
    font-size: 16px;
    line-height: 22px;
    font-family: ${({ theme }) => theme.typography.fontSecondary};
    background-color: ${({ theme }) => theme.colors.grey500};
    width: 100%;

    a {
        margin-right: 5px;
    }
`;

const StyledCta = styled.div`
    margin: 0 0 32px;
    padding: 0 20px;
    text-align: center;

    h2 {
        margin: 0 0 15px;
    }

    button {
        width: 100%;
        max-width: 264px;
        display: inline-flex;
    }
`;

const StyledLiveAuctions = styled.div`
    margin: 0 auto 32px;
    padding: 0 20px;
    max-width: 1200px;

    h2 {
        text-align: center;
        margin: 0;
    }
`;
